import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import type { ITitle } from "containers/Titles/types";
import type { DTOCreateWork, DTOUpdateWork, IWork, IWorkTitleSummary } from "containers/Works/types";
import { downloadApiExport, getDateForFileName, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";
import { type CreatedTermListEntry, insertCreatedTerm } from "store/createdTerm";
import type { ITerm } from "store/managementStore";
import { deleteTag, fetchUpdateTitle, postNewTags } from "store/titleStore";
import {
    appendTitleTag,
    buildTagFromPostPayload,
    normalizeTitleTags,
    patchTitleTagsInWork,
    removeTitleTag,
    type TitleTag,
} from "store/titleTagSync";
import type { TFetchStatus, TParamsPagination } from "store/types";

const api = () => import.meta.env.REACT_APP_API_ENDPOINT;

export const WORKS_PAGE_SIZE = 50;

const axiosErrorPayload = (error: unknown) => (axios.isAxiosError(error) ? error.response?.data : undefined);

const toastFromPayload = (payload: unknown, toastId: string) => {
    const message =
        payload && typeof payload === "object" && "message" in payload
            ? String((payload as { message?: string }).message)
            : undefined;
    toast.error(translationsWithFallback(message ?? "", "ServerError.InternalServerError"), { toastId });
};

const withTitles = (work: IWork): IWork => ({
    ...work,
    titles: (work.titles ?? []).map((title) => ({
        ...title,
        tags: normalizeTitleTags(title.tags),
    })),
});

type FetchWorksResponse = {
    entries: IWork[];
    count: number;
    offset: number;
};

export const fetchWorks = createAsyncThunk(
    "works/getWorks",
    async (options: TParamsPagination | undefined, { rejectWithValue }) => {
        try {
            const offset = options?.offset ?? 0;
            const response = await axios.get<{ data: FetchWorksResponse }>(`${api()}/work`, {
                params: {
                    limit: options?.limit ?? WORKS_PAGE_SIZE,
                    offset,
                    query: options?.query || undefined,
                    sortBy: options?.sortBy,
                    sortOrder: options?.sortOrder,
                    tags: options?.tags,
                },
            });
            return { ...response.data.data, offset };
        } catch (error) {
            return rejectWithValue(axiosErrorPayload(error));
        }
    },
);

export const exportWorkList = createAsyncThunk("works/exportWorkList", async () => {
    const response = await toast.promise(
        axios.get(`${api()}/work/export`, {
            headers: { Accept: "*/*" },
        }),
        {
            pending: `${t("Export.Pending")}`,
            success: `${t("Export.Successful")}`,
            error: `${t("Export.Error")}`,
        },
    );

    const fileName = `export_works_${getDateForFileName()}.xlsx`;
    downloadApiExport(response.data, fileName);
});

export const fetchWorkNames = async (): Promise<Pick<IWork, "id" | "name" | "workCode">[]> => {
    const response = await axios.get<{ data: Pick<IWork, "id" | "name" | "workCode">[] }>(`${api()}/work/names`);
    return response.data.data ?? [];
};

export const fetchWork = createAsyncThunk("works/getWork", async (id: number, { rejectWithValue }) => {
    try {
        const response = await axios.get<{ data: IWork }>(`${api()}/work/${id}`);
        return response.data.data;
    } catch (error) {
        return rejectWithValue(axiosErrorPayload(error));
    }
});

export const createWork = createAsyncThunk("works/createWork", async (payload: DTOCreateWork, { rejectWithValue }) => {
    try {
        const response = await axios.post<{ data: IWork }>(`${api()}/work`, payload);
        return response.data.data;
    } catch (error) {
        return rejectWithValue(axiosErrorPayload(error));
    }
});

export const updateWork = createAsyncThunk(
    "works/updateWork",
    async ({ id, payload }: { id: number; payload: DTOUpdateWork }, { rejectWithValue }) => {
        try {
            const response = await axios.put<{ data: IWork }>(`${api()}/work/${id}`, payload);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(axiosErrorPayload(error));
        }
    },
);

export const fetchWorkTerms = createAsyncThunk("works/fetchWorkTerms", async (workId: number, { rejectWithValue }) => {
    try {
        const response = await axios.get<{ result: ITerm[] }>(`${api()}/work/${workId}/terms`);
        return response.data.result ?? [];
    } catch (error) {
        return rejectWithValue(axiosErrorPayload(error));
    }
});

export const deleteWork = createAsyncThunk("works/deleteWork", async (id: number, { getState, rejectWithValue }) => {
    try {
        await axios.delete(`${api()}/work/${id}`);
        const state = getState() as { works: { selectedWork: IWork | null; works: IWork[] } };
        const work =
            state.works.selectedWork?.id === id
                ? state.works.selectedWork
                : state.works.works.find((item) => item.id === id);
        return { id, titleIds: work?.titles.map((title) => title.id) ?? [] };
    } catch (error) {
        return rejectWithValue(axiosErrorPayload(error));
    }
});

export interface WorkState {
    works: IWork[];
    worksStatus: TFetchStatus;
    countWorks: number;
    fetchWorksReqId: string;
    isListRefreshing: boolean;
    selectedWork: IWork | null;
    selectedWorkStatus: TFetchStatus;
    fetchWorkReqId: string;
    createWorkStatus: TFetchStatus;
    updateWorkStatus: TFetchStatus;
    deleteWorkStatus: TFetchStatus;
    workTerms: ITerm[];
    workTermsStatus: TFetchStatus;
    workTermsWorkId: number | null;
    fetchWorkTermsReqId: string;
}

const summaryFromUpdatedTitle = (title: ITitle, current?: IWorkTitleSummary): IWorkTitleSummary => ({
    id: Number(title.id),
    ean13: title.ean13 ?? current?.ean13 ?? "",
    name: title.name ?? current?.name ?? "",
    avatarUrl: title.avatarUrl ?? current?.avatarUrl,
    releaseDate: title.releaseDate ?? current?.releaseDate,
    tags: title.tags != null ? title.tags : current?.tags,
    formatTypeId: title.formatTypeId !== undefined ? title.formatTypeId : current?.formatTypeId,
    formatTypeName: title.formatTypeName !== undefined ? title.formatTypeName : current?.formatTypeName,
    customFields: current?.customFields,
});

/** Keep the works list in sync with a title update response, without refetching the list. */
const applyUpdatedTitleToWorks = (state: WorkState, title: ITitle) => {
    const titleId = Number(title.id);
    const nextWorkId = title.workId == null ? null : Number(title.workId);

    const applyToWork = (work: IWork): IWork => {
        const existing = work.titles.find((item) => Number(item.id) === titleId);
        const isTargetWork = nextWorkId != null && Number(work.id) === nextWorkId;
        if (!isTargetWork) {
            if (!existing) return work;
            return { ...work, titles: work.titles.filter((item) => Number(item.id) !== titleId) };
        }
        const summary = summaryFromUpdatedTitle(title, existing);
        if (!existing) return { ...work, titles: [...work.titles, summary] };
        return {
            ...work,
            titles: work.titles.map((item) => (Number(item.id) === titleId ? summary : item)),
        };
    };

    state.works = state.works.map(applyToWork);
    if (state.selectedWork) state.selectedWork = applyToWork(state.selectedWork);
};

const syncTitleTagsInWorks = (state: WorkState, titleId: number, patchTags: (tags: unknown) => TitleTag[]) => {
    state.works = state.works.map((work) => patchTitleTagsInWork(work, titleId, patchTags));
    if (state.selectedWork) {
        state.selectedWork = patchTitleTagsInWork(state.selectedWork, titleId, patchTags);
    }
};

const initialState: WorkState = {
    works: [],
    worksStatus: "idle",
    countWorks: 0,
    fetchWorksReqId: "",
    isListRefreshing: false,
    selectedWork: null,
    selectedWorkStatus: "idle",
    fetchWorkReqId: "",
    createWorkStatus: "idle",
    updateWorkStatus: "idle",
    deleteWorkStatus: "idle",
    workTerms: [],
    workTermsStatus: "idle",
    workTermsWorkId: null,
    fetchWorkTermsReqId: "",
};

const workSlice = createSlice({
    name: "works",
    initialState,
    reducers: {
        clearSelectedWork(state) {
            state.selectedWork = null;
            state.selectedWorkStatus = "idle";
        },
        resetCreateWorkStatus(state) {
            state.createWorkStatus = "idle";
        },
        resetUpdateWorkStatus(state) {
            state.updateWorkStatus = "idle";
        },
        resetDeleteWorkStatus(state) {
            state.deleteWorkStatus = "idle";
        },
        invalidateWorks(state) {
            state.worksStatus = "idle";
        },
        resetWorks(state) {
            state.works = [];
            state.worksStatus = "idle";
            state.countWorks = 0;
            state.fetchWorksReqId = "";
            state.isListRefreshing = false;
            state.selectedWork = null;
            state.selectedWorkStatus = "idle";
            state.workTerms = [];
            state.workTermsStatus = "idle";
            state.workTermsWorkId = null;
            state.fetchWorkTermsReqId = "";
            state.fetchWorkReqId = "";
        },
        removeWorkTerm(state, action: PayloadAction<number>) {
            state.workTerms = state.workTerms.filter((term) => term.id !== action.payload);
        },
        addCreatedTerm(state, action: PayloadAction<CreatedTermListEntry>) {
            const workId = action.payload.workId;
            if (workId == null || state.workTermsWorkId !== workId || state.workTermsStatus !== "succeeded") return;
            state.workTerms = insertCreatedTerm(state.workTerms, action.payload, {
                avatar: "beneficiary",
                sortKey: (term) => term.author,
            });
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchWorks.pending, (state, action) => {
            state.fetchWorksReqId = action.meta.requestId;
            state.worksStatus = "pending";
            if (!action.meta.arg?.offset) {
                state.isListRefreshing = true;
            }
        });
        builder.addCase(fetchWorks.fulfilled, (state, action) => {
            if (state.fetchWorksReqId !== action.meta.requestId) return;
            state.worksStatus = "succeeded";
            state.isListRefreshing = false;
            const mapped = action.payload.entries.map(withTitles);
            if (action.payload.offset) {
                const existingIds = new Set(state.works.map((work) => work.id));
                state.works = [...state.works, ...mapped.filter((work) => !existingIds.has(work.id))];
            } else {
                state.works = mapped;
            }
            state.countWorks = Number(action.payload.count);
            if (state.selectedWork) {
                const refreshed = mapped.find((item) => Number(item.id) === Number(state.selectedWork?.id));
                if (refreshed) {
                    state.selectedWork = refreshed;
                }
            }
        });
        builder.addCase(fetchWorks.rejected, (state, { payload, meta }) => {
            if (state.fetchWorksReqId !== meta.requestId) return;
            state.worksStatus = "failed";
            state.isListRefreshing = false;
            toastFromPayload(payload, "works.fetch");
        });

        builder.addCase(fetchWork.pending, (state, action) => {
            state.fetchWorkReqId = action.meta.requestId;
            state.selectedWorkStatus = "pending";
        });
        builder.addCase(fetchWork.fulfilled, (state, action) => {
            // Several views fetch into this one slot: a slower, older response must not replace the latest work.
            if (state.fetchWorkReqId !== action.meta.requestId) return;
            state.selectedWorkStatus = "succeeded";
            const work = withTitles(action.payload);
            state.selectedWork = work;
            state.works = state.works.map((item) => (Number(item.id) === Number(work.id) ? work : item));
        });
        builder.addCase(fetchWork.rejected, (state, { payload, meta }) => {
            if (state.fetchWorkReqId !== meta.requestId) return;
            state.selectedWorkStatus = "failed";
            toastFromPayload(payload, "works.fetchOne");
        });

        builder.addCase(createWork.pending, (state) => {
            state.createWorkStatus = "pending";
        });
        builder.addCase(createWork.fulfilled, (state, action) => {
            state.createWorkStatus = "succeeded";
            const work = withTitles(action.payload);
            state.works = [work, ...state.works.filter((item) => item.id !== work.id)];
            state.countWorks += 1;
            state.selectedWork = work;
            toast.success(`${t("Works.WorkCreated")}`, { toastId: "works.create" });
        });
        builder.addCase(createWork.rejected, (state, { payload }) => {
            state.createWorkStatus = "failed";
            toastFromPayload(payload, "works.create");
        });

        builder.addCase(updateWork.pending, (state) => {
            state.updateWorkStatus = "pending";
        });
        builder.addCase(updateWork.fulfilled, (state, action) => {
            state.updateWorkStatus = "succeeded";
            const work = withTitles({
                ...action.payload,
                titles: action.payload.titles ?? state.selectedWork?.titles ?? [],
            });
            state.works = state.works.map((item) => (item.id === work.id ? work : item));
            if (state.selectedWork?.id === work.id) {
                state.selectedWork = work;
            }
            toast.success(`${t("Works.WorkUpdated")}`, { toastId: "works.update" });
        });
        builder.addCase(updateWork.rejected, (state, { payload }) => {
            state.updateWorkStatus = "failed";
            toastFromPayload(payload, "works.update");
        });

        builder.addCase(deleteWork.pending, (state) => {
            state.deleteWorkStatus = "pending";
        });
        builder.addCase(deleteWork.fulfilled, (state, action) => {
            state.deleteWorkStatus = "succeeded";
            const workId = action.payload.id;
            const removed = state.works.find((work) => work.id === workId);
            state.works = state.works.filter((work) => work.id !== workId);
            if (removed) state.countWorks = Math.max(0, state.countWorks - 1);
            if (state.selectedWork?.id === workId) {
                state.selectedWork = null;
                state.selectedWorkStatus = "idle";
            }
            toast.success(`${t("Works.WorkDeleted")}`, { toastId: "works.delete" });
        });
        builder.addCase(deleteWork.rejected, (state, { payload }) => {
            state.deleteWorkStatus = "failed";
            toastFromPayload(payload, "works.delete");
        });

        builder.addCase(fetchWorkTerms.pending, (state, action) => {
            state.fetchWorkTermsReqId = action.meta.requestId;
            state.workTermsStatus = "pending";
            if (state.workTermsWorkId !== action.meta.arg) {
                state.workTerms = [];
                state.workTermsWorkId = action.meta.arg;
            }
        });
        builder.addCase(fetchWorkTerms.fulfilled, (state, action) => {
            if (state.fetchWorkTermsReqId !== action.meta.requestId) return;
            state.workTermsStatus = "succeeded";
            state.workTerms = action.payload;
            state.workTermsWorkId = action.meta.arg;
        });
        builder.addCase(fetchWorkTerms.rejected, (state, { payload, meta }) => {
            if (state.fetchWorkTermsReqId !== meta.requestId) return;
            state.workTermsStatus = "failed";
            toastFromPayload(payload, "works.terms");
        });

        builder.addCase(fetchUpdateTitle.fulfilled, (state, action) => {
            const title = action.payload?.data as ITitle | undefined;
            if (title?.id == null) return;
            applyUpdatedTitleToWorks(state, title);
        });

        builder.addCase(postNewTags.fulfilled, (state, action) => {
            if (!action.payload.titleId) return;
            const titleId = +action.payload.titleId;
            const newTag = buildTagFromPostPayload(action.payload);
            syncTitleTagsInWorks(state, titleId, (tags) => appendTitleTag(tags, newTag));
        });

        builder.addCase(deleteTag.fulfilled, (state, action) => {
            if (action.payload.titleId === undefined) return;
            const titleId = +action.payload.titleId;
            const tagId = +action.payload.tagId;
            syncTitleTagsInWorks(state, titleId, (tags) => removeTitleTag(tags, tagId));
        });
    },
});

export const {
    clearSelectedWork,
    resetCreateWorkStatus,
    resetUpdateWorkStatus,
    resetDeleteWorkStatus,
    invalidateWorks,
    resetWorks,
    removeWorkTerm,
    addCreatedTerm,
} = workSlice.actions;
export default workSlice.reducer;
